import { useMutateCreateNewSession } from '@/hooks/mutation/useMutateCreateNewSession';
import { useMutateLinkSessionsToPatientProfile } from '@/hooks/mutation/useMutateLinkSessionsToPatientProfile';
import { patientViewIdAtom } from '@heidi/patients';
import type {
  PatientProfile,
  PatientProfileWithSessionCount,
} from '@heidi/patients/contracts';
import { SessionCreatedEntryPoint } from '@heidi/session/contracts';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

/**
 * Creates a session, links it to the patient, then navigates to it.
 * Shared by the drawer's footer CTA and the Session History section
 * header button so they stay behaviourally identical.
 */
interface UseCreatePatientSessionOptions {
  sessionRoute?: 'legacy' | 'v2';
}

export const useCreatePatientSession = (
  patient: PatientProfileWithSessionCount | undefined,
  onDone: () => void,
  { sessionRoute = 'legacy' }: UseCreatePatientSessionOptions = {}
) => {
  const router = useRouter();
  // The active patient-profile view's id, stamped on session_created so the
  // open -> session funnel stitches per-view. Null outside an open view.
  const viewId = useAtomValue(patientViewIdAtom);
  const { mutateAsync: createNewSession, isPending } = useMutateCreateNewSession({
    navigate: false,
    entryPoint: SessionCreatedEntryPoint.PatientSurface,
    viewId: viewId ?? undefined,
  });
  const { mutateAsync: linkSessions } = useMutateLinkSessionsToPatientProfile();

  const createSession = useCallback(async () => {
    if (!patient?.id) return;
    let newSessionId: string | undefined;
    try {
      const response = await createNewSession();
      newSessionId = response?.data?.session_id;
      if (!newSessionId) return;
      await linkSessions({
        patientProfile: patient as unknown as PatientProfile,
        sessionIds: [newSessionId],
        // The new session has no note, so the recap can't have changed, and we
        // navigate away from the profile on the next line regardless.
        deferRecapRefetch: true,
      });
    } catch {
      // Both mutations already log a sanitized error and show the destructive
      // toast in their own onError, so settling here adds no second toast and
      // no second log. The callers fire this as `void createSession()`, so a
      // rethrow reaches the global unhandled-rejection handler
      // (HEIDI-DESKTOP-W3K, DSK-1318). The drawer stays open on the patient
      // and nothing navigates, so the user can retry. Only the two mutation
      // calls are guarded: a failure in onDone or navigation still propagates.
      return;
    }
    onDone();
    const sessionPath = sessionRoute === 'v2' ? 'sessions' : 'session';
    void router.push(`/scribe/${sessionPath}/${newSessionId}`);
  }, [patient, createNewSession, linkSessions, onDone, router, sessionRoute]);

  return { createSession, isPending, disabled: !patient?.id };
};
